import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import { colors } from "../../../ui/colors";
import { Autocomplete } from "@react-google-maps/api";
import { useRef } from "react";
import { businessStructures, companyRoles } from "../constants/business.constants";
import { inputStyles } from "../../../ui/styles/inputStyles";
import { gateway } from "../../../../gateway";

export const BusinessInformationForm = ({ control, errors }) => {
  const autocompleteRef = useRef(null);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          color: colors.gray.base,
          fontWeight: 600,
          fontSize: "20px",
        }}
      >
        Tell us about your business - Just a few steps!
      </Typography>

      {/* Company name + EIN */}
      <Box sx={{ display: "flex", gap: 5, mb: 4 }}>
        <Controller
          name="companyName"
          control={control}
          rules={{ required: "Company name is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Company Name"
              error={!!errors.companyName}
              helperText={errors.companyName?.message}
              sx={inputStyles}
            />
          )}
        />

        <Controller
          name="employerIdNumber"
          control={control}
          rules={{
            required: "Employer Identification Number is required",
            minLength: { value: 9, message: "Must be exactly 9 digits" },
            pattern: {
              value: /^[0-9]{9}$/,
              message: "Must be exactly 9 digits",
            },
            validate: async (value) => {
              if (!value || value.length !== 9) return true;
              try {
                const response = await gateway.get(`/users/isemployeridavailable/${value}`);
                if (response?.data?.data?.isEmployerIdAvailable === false) {
                  return "Employer Identification Number is already in use";
                }
                return true;
              } catch (error) {
                return "Unable to verify EIN availability. Please try again.";
              }
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Employer Identification Number"
              error={!!errors.employerIdNumber}
              required={true}
              helperText={errors.employerIdNumber?.message || "Required: You can enter a temporary EIN and update it later in your account settings."}
              inputProps={{ maxLength: 9, inputMode: "numeric" }}
              sx={inputStyles}
              onChange={(e) => {
                const numeric = e.target.value.replace(/\D/g, "");
                field.onChange(numeric);
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ gap: 5, mb: 4 }}>
        <Controller
          name="mailingAddress"
          control={control}
          rules={{ required: "Mailing address is required" }}
          render={({ field }) => (
            <Autocomplete
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={() => {
                if (autocompleteRef.current) {
                  const place = autocompleteRef.current.getPlace();
                  field.onChange(place.formatted_address || "");
                }
              }}
            >
              <TextField
                {...field}
                fullWidth
                label="Mailing Address"
                error={!!errors.mailingAddress}
                helperText={errors.mailingAddress?.message}
                sx={inputStyles}
              />
            </Autocomplete>
          )}
        />
      </Box>

      {/* Role + Structure */}
      <Box sx={{ display: "flex", gap: 5, mb: 2 }}>
        <Controller
          name="companyRole"
          control={control}
          rules={{ required: "Please select your role" }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              label="Your Company Role"
              error={!!errors.companyRole}
              helperText={errors.companyRole?.message}
              sx={inputStyles}
            >
              <MenuItem value="" disabled>
                Choose an option
              </MenuItem>
              {companyRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          name="businessStructure"
          control={control}
          rules={{ required: "Please select business structure" }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              label="Business Structure"
              error={!!errors.businessStructure}
              helperText={errors.businessStructure?.message}
              sx={inputStyles}
            >
              <MenuItem value="" disabled>
                Choose an option
              </MenuItem>
              {businessStructures.map((structure) => (
                <MenuItem key={structure} value={structure}>
                  {structure}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>
    </Box>
  );
};
